import { useState } from 'react';
import { CmmFile } from '../../../shared/types/cmmReport.ts';
import { useCmmFileSelect } from '../../hooks/useCmmFileSelect.ts';
import { importCmmFiles } from '../../services/cmm-import/importCmmFiles.ts';
import { CmmFileImport, CmmPositionRead, FileSource } from '../../services/cmm-import/types.ts';
import { CmmConfig, Protocol, RecordDraft, RecordPosition } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { useCmmValuesApply } from './useCmmValuesApply.ts';

interface Props {
    protocol: Protocol;
    record: RecordDraft;
    partIndex: number;
    cmmConfigs: CmmConfig[] | undefined;
    setRecord: (record: RecordDraft) => void;
    setIsDirty: (isDirty: boolean) => void;
}

export function useCmmImport({ protocol, record, partIndex, cmmConfigs, setRecord, setIsDirty }: Props) {
    const [positionsDialogState, setPositionsDialogState] = useState<CmmFileImport[] | null>(null);
    const { data: cmmEquipments } = trpc.equipments.getAllCustom.useQuery({ type: 'cmm/vmm' });

    /** State for the multi-part import dialog (IOQC only) */
    const [multiPartDialogState, setMultiPartDialogState] = useState<{
        partGroups: CmmPositionRead[][];
        equipmentId: string;
        filename: string;
        fileSource: FileSource;
    } | null>(null);

    // Apply CMM report values once the files and positions have been selected
    const { attemptApplyAutoImport, applyImportValues, applyMultiPartValues } = useCmmValuesApply({
        protocol,
        record,
        partIndex,
        cmmConfigs,
        setRecord,
        setIsDirty,
    });

    // Select CMM reports to import
    const {
        isAutoImportClicked,
        isManualImportClicked,
        filesDialogState,
        handleAutoImportClick,
        handleManualImportClick,
        handleLocalFilesSelected,
        handleFilesDialogSubmit,
        handleFilesDialogCancel,
    } = useCmmFileSelect({
        partName: protocol.partName,
        orderNumber: record.orderNumber,
        partCount: record.partCount,
        onFilesSelected,
        attemptApplyAutoImport,
    });

    async function onFilesSelected(valueSource: FileSource, cmmReports: CmmFile[]) {
        if (!cmmConfigs) {
            alert('Failed to load CMM configurations.');
            return;
        }

        try {
            const files = cmmReports.map((it) => it.file);
            const importResults = await importCmmFiles(
                protocol,
                record,
                partIndex,
                cmmConfigs,
                files,
                valueSource,
                { ignoreInvalidPosNr: true },
                cmmEquipments,
            );

            // For IOQC records: when the file contains multiple parts (detected by repeating
            // position numbers) route to the dedicated multi-part dialog instead.
            if (record.measurementType === 'ioqc') {
                const multiPartResult = importResults.find((r) => r.rawPartGroups && r.rawPartGroups.length > 1);
                if (multiPartResult) {
                    setMultiPartDialogState({
                        partGroups: multiPartResult.rawPartGroups!,
                        equipmentId: multiPartResult.equipmentId,
                        filename: multiPartResult.filename,
                        fileSource: valueSource,
                    });
                    return;
                }
            }

            setPositionsDialogState(importResults);
        } catch (error) {
            alert('Failed to fetch CMM reports.');
            console.error('importCmmFiles error:', error);
        }
    }

    async function handleMultiPartSubmit(startPartIndex: number, expandToFit: boolean, includePartiallyFailed: boolean) {
        if (!multiPartDialogState) return;
        const { partGroups, equipmentId, fileSource, filename } = multiPartDialogState;
        try {
            const sharePointFiles = fileSource === 'sharepoint' ? [filename] : [];
            await applyMultiPartValues(partGroups, equipmentId, startPartIndex, expandToFit, includePartiallyFailed, sharePointFiles);
            setMultiPartDialogState(null);
        } catch (error) {
            alert('Failed to apply multi-part import.');
            console.error('handleMultiPartSubmit error:', error);
        }
    }

    function handlePositionsDialogSubmit(importValues: RecordPosition[], sharePointFiles: string[]) {
        applyImportValues(importValues, sharePointFiles, true);
        setPositionsDialogState(null);
    }

    return {
        filesDialogState,
        positionsDialogState,
        multiPartDialogState,
        isAutoImportClicked,
        isManualImportClicked,
        handleAutoImportClick,
        handleManualImportClick,
        handleLocalFilesSelected,
        handleFilesDialogSubmit,
        handleFilesDialogCancel,
        handlePositionsDialogSubmit,
        handlePositionsDialogCancel: () => setPositionsDialogState(null),
        handleMultiPartSubmit,
        handleMultiPartCancel: () => setMultiPartDialogState(null),
    };
}
